/**
 * Pure geometry helpers for board renderers. No React, no DOM, no three.js: these are unit-tested directly
 * (see `packages/modules/test/renderers/layout.test.ts`) and consumed by `Board2D.tsx` to turn a `BoardGraph`
 * plus in-flight `MoveEvent`s into 2D screen coordinates. Coordinates here are an abstract 2D plane; callers
 * decide which board axes (e.g. x/z) map onto them.
 */

export interface Point2D {
  x: number;
  y: number;
}

export interface RingLayoutOptions {
  /** Ring radius in the same abstract units as the returned points. Defaults to 1. */
  radius?: number;
  /** Ring centre. Defaults to the origin. */
  center?: Point2D;
  /** Angle (radians) of the first id. Defaults to -PI/2 (straight up), so layouts read clockwise from the top. */
  startAngleRad?: number;
}

/**
 * Places `ids` evenly around a circle, in order, starting at `startAngleRad` and proceeding clockwise.
 * Used whenever a board graph omits positions (`BoardSpace.position === null`) so it still renders sensibly.
 * A single id (or zero ids) never divides by zero: with one id it sits at `startAngleRad`.
 */
export function ringLayout(ids: string[], options: RingLayoutOptions = {}): Record<string, Point2D> {
  const radius = options.radius ?? 1;
  const center = options.center ?? { x: 0, y: 0 };
  const startAngleRad = options.startAngleRad ?? -Math.PI / 2;
  const n = ids.length;
  const result: Record<string, Point2D> = {};
  ids.forEach((id, index) => {
    const angle = n === 0 ? startAngleRad : startAngleRad + (index / n) * Math.PI * 2;
    result[id] = {
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
    };
  });
  return result;
}

export interface BoxSize {
  width: number;
  height: number;
  padding: number;
}

/**
 * Uniformly scales and centres a set of points so they fill `box` (minus `padding`) without distortion.
 * Used to map board-unit or ring-layout coordinates onto an SVG viewBox. A degenerate span (all points share
 * an x or y) falls back to a span of 1 so the scale stays finite.
 */
export function fitPointsToBox(points: Record<string, Point2D>, box: BoxSize): Record<string, Point2D> {
  const entries = Object.entries(points);
  if (entries.length === 0) return {};
  const xs = entries.map(([, p]) => p.x);
  const ys = entries.map(([, p]) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const spanX = maxX - minX || 1;
  const spanY = maxY - minY || 1;
  const innerW = Math.max(box.width - box.padding * 2, 0);
  const innerH = Math.max(box.height - box.padding * 2, 0);
  const scale = Math.min(innerW / spanX, innerH / spanY);
  const offsetX = box.padding + (innerW - spanX * scale) / 2;
  const offsetY = box.padding + (innerH - spanY * scale) / 2;

  const out: Record<string, Point2D> = {};
  for (const [id, p] of entries) {
    out[id] = {
      x: offsetX + (p.x - minX) * scale,
      y: offsetY + (p.y - minY) * scale,
    };
  }
  return out;
}

export interface PathPosition {
  position: Point2D;
  /** True once `elapsedMs` has reached the end of the last hop. */
  done: boolean;
}

/**
 * Interpolates a token's on-screen position along a hop-by-hop path. `sequence` is the full list of points
 * the token passes through, starting with its current position (`sequence[0]`) followed by one point per
 * space in `MoveEvent.path`. Each hop takes `msPerHop`. Used to animate `board.move` locally; the canonical
 * position (`BoardToken.spaceId` in the next state) is authoritative regardless of what this returns.
 */
export function pathPositionAt(sequence: Point2D[], elapsedMs: number, msPerHop: number): PathPosition {
  if (sequence.length === 0) return { position: { x: 0, y: 0 }, done: true };
  const hops = sequence.length - 1;
  if (hops <= 0 || msPerHop <= 0) return { position: sequence[sequence.length - 1], done: true };

  const totalMs = hops * msPerHop;
  const clamped = Math.min(Math.max(elapsedMs, 0), totalMs);
  const hopIndex = Math.min(Math.floor(clamped / msPerHop), hops - 1);
  const localT = (clamped - hopIndex * msPerHop) / msPerHop;
  const a = sequence[hopIndex];
  const b = sequence[hopIndex + 1];
  return {
    position: { x: a.x + (b.x - a.x) * localT, y: a.y + (b.y - a.y) * localT },
    done: clamped >= totalMs,
  };
}
